import React, { useState, useEffect } from 'react';
import { Clock, Send, Zap, Users, TrendingUp, ChevronLeft, ChevronRight, Filter, CheckCircle2, AlertTriangle, Bell } from 'lucide-react';
import api from '../../utils/api';

/**
 * ReminderHistory — shows chronological log of all reminders sent,
 * effectiveness metrics, and filtering by activity type / time period.
 * Used in the OverviewAnalyticsDrawer.
 */
const ReminderHistory = () => {
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState(null);
    const [page, setPage] = useState(1);
    const [filters, setFilters] = useState({
        activityType: '',
        reminderType: '',
        period: '30d'
    });

    useEffect(() => {
        fetchHistory();
    }, [page, filters]);

    const fetchHistory = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const params = new URLSearchParams({ page, limit: 15 });
            if (filters.activityType) params.append('activityType', filters.activityType);
            if (filters.reminderType) params.append('reminderType', filters.reminderType);
            if (filters.period) params.append('period', filters.period);

            const res = await api.get(`/reminders/company-history?${params}`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            setData(res.data);
        } catch (err) {
            console.error('Failed to load reminder history:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleFilterChange = (key, value) => {
        setFilters(prev => ({ ...prev, [key]: value }));
        setPage(1);
    };

    const activityTypeLabel = (type) => {
        const labels = { task: 'Task', survey: 'Survey', compliance: 'Compliance', course_item: 'Course' };
        return labels[type] || type;
    };

    const activityTypeColor = (type) => {
        const colors = {
            task: 'bg-blue-100 text-blue-700',
            survey: 'bg-purple-100 text-purple-700',
            compliance: 'bg-red-100 text-red-700',
            course_item: 'bg-green-100 text-green-700'
        };
        return colors[type] || 'bg-gray-100 text-gray-700';
    };

    const effectiveness = data?.effectiveness;
    const history = data?.history || [];
    const pagination = data?.pagination;

    return (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            {/* Header */}
            <div className="px-5 py-4 border-b border-gray-100">
                <div className="flex items-center gap-2.5 mb-3">
                    <div className="p-1.5 bg-indigo-50 rounded-lg">
                        <Clock className="w-4 h-4 text-indigo-600" />
                    </div>
                    <h3 className="font-semibold text-gray-800 text-sm">Reminder History & Effectiveness</h3>
                </div>

                {/* Filters */}
                <div className="flex flex-wrap gap-2">
                    <select
                        value={filters.period}
                        onChange={e => handleFilterChange('period', e.target.value)}
                        className="text-xs border border-gray-200 rounded-lg px-2.5 py-1.5 bg-gray-50 text-gray-700 focus:ring-1 focus:ring-blue-300 focus:outline-none"
                    >
                        <option value="7d">Last 7 days</option>
                        <option value="30d">Last 30 days</option>
                        <option value="90d">Last 90 days</option>
                        <option value="all">All time</option>
                    </select>
                    <select
                        value={filters.activityType}
                        onChange={e => handleFilterChange('activityType', e.target.value)}
                        className="text-xs border border-gray-200 rounded-lg px-2.5 py-1.5 bg-gray-50 text-gray-700 focus:ring-1 focus:ring-blue-300 focus:outline-none"
                    >
                        <option value="">All Activity Types</option>
                        <option value="task">Tasks</option>
                        <option value="survey">Surveys</option>
                        <option value="compliance">Compliance</option>
                        <option value="course_item">Courses</option>
                    </select>
                    <select
                        value={filters.reminderType}
                        onChange={e => handleFilterChange('reminderType', e.target.value)}
                        className="text-xs border border-gray-200 rounded-lg px-2.5 py-1.5 bg-gray-50 text-gray-700 focus:ring-1 focus:ring-blue-300 focus:outline-none"
                    >
                        <option value="">All Types</option>
                        <option value="manual">Manual</option>
                        <option value="automated">Automated</option>
                    </select>
                </div>
            </div>

            {/* Effectiveness Metrics */}
            {effectiveness && effectiveness.totalReminders > 0 && (
                <div className="px-5 py-3 border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white">
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                        <div className="text-center">
                            <p className="text-lg font-bold text-gray-800">{effectiveness.totalReminders}</p>
                            <p className="text-[11px] text-gray-500">Total Sent</p>
                        </div>
                        <div className="text-center">
                            <p className="text-lg font-bold text-blue-700">{effectiveness.totalRecipients}</p>
                            <p className="text-[11px] text-gray-500">Total Recipients</p>
                        </div>
                        <div className="text-center">
                            <p className="text-lg font-bold text-green-700">{effectiveness.completedWithin48h}</p>
                            <p className="text-[11px] text-gray-500">Completed (48h)</p>
                        </div>
                        <div className="text-center">
                            <p className={`text-lg font-bold ${effectiveness.overallEffectiveness >= 50 ? 'text-green-700' : effectiveness.overallEffectiveness >= 25 ? 'text-amber-700' : 'text-red-700'}`}>
                                {effectiveness.overallEffectiveness}%
                            </p>
                            <p className="text-[11px] text-gray-500">Effectiveness</p>
                        </div>
                    </div>
                </div>
            )}

            {/* History List */}
            <div className="divide-y divide-gray-50">
                {loading ? (
                    <div className="py-8 text-center text-gray-500 text-sm">Loading history...</div>
                ) : history.length === 0 ? (
                    <div className="py-8 text-center">
                        <Bell className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                        <p className="text-sm text-gray-500">No reminders sent yet</p>
                        <p className="text-xs text-gray-400 mt-1">Reminders will appear here once they're sent</p>
                    </div>
                ) : (
                    history.map((r) => (
                        <div key={r.id} className="px-5 py-3 hover:bg-gray-50 transition-colors">
                            <div className="flex items-start justify-between gap-3">
                                <div className="flex items-start gap-3 min-w-0 flex-1">
                                    <div className={`mt-0.5 p-1.5 rounded-lg flex-shrink-0 ${r.reminder_type === 'automated' ? 'bg-purple-50' : 'bg-blue-50'}`}>
                                        {r.reminder_type === 'automated'
                                            ? <Zap className="w-3.5 h-3.5 text-purple-600" />
                                            : <Send className="w-3.5 h-3.5 text-blue-600" />
                                        }
                                    </div>
                                    <div className="min-w-0 flex-1">
                                        <p className="text-sm font-medium text-gray-800 truncate">
                                            {r.content_preview || 'Reminder sent'}
                                        </p>
                                        <div className="flex items-center flex-wrap gap-1.5 mt-1">
                                            <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${activityTypeColor(r.activity_type)}`}>
                                                {activityTypeLabel(r.activity_type)}
                                            </span>
                                            <span className="text-[11px] text-gray-400">
                                                {r.recipient_count} recipient{r.recipient_count !== 1 ? 's' : ''}
                                            </span>
                                            <span className="text-[11px] text-gray-300">·</span>
                                            <span className="text-[11px] text-gray-400">
                                                {r.sender_name}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                                <div className="text-right flex-shrink-0">
                                    <p className="text-[11px] text-gray-400">
                                        {new Date(r.sent_at).toLocaleDateString()}
                                    </p>
                                    {r.effectiveness_pct !== null && r.effectiveness_pct !== undefined && r.recipient_count > 0 && (
                                        <div className="flex items-center gap-1 mt-1 justify-end">
                                            {parseInt(r.effectiveness_pct) > 0 ? (
                                                <CheckCircle2 className="w-3 h-3 text-green-500" />
                                            ) : (
                                                <AlertTriangle className="w-3 h-3 text-amber-500" />
                                            )}
                                            <span className={`text-[11px] font-medium ${parseInt(r.effectiveness_pct) > 0 ? 'text-green-600' : 'text-amber-600'}`}>
                                                {r.effectiveness_pct}%
                                            </span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))
                )}
            </div>

            {/* Pagination */}
            {pagination && pagination.totalPages > 1 && (
                <div className="px-5 py-3 border-t border-gray-100 flex items-center justify-between">
                    <span className="text-xs text-gray-500">
                        Page {pagination.page} of {pagination.totalPages} ({pagination.total} total)
                    </span>
                    <div className="flex gap-1">
                        <button
                            onClick={() => setPage(p => Math.max(1, p - 1))}
                            disabled={pagination.page <= 1}
                            className="p-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                        >
                            <ChevronLeft className="w-3.5 h-3.5 text-gray-600" />
                        </button>
                        <button
                            onClick={() => setPage(p => Math.min(pagination.totalPages, p + 1))}
                            disabled={pagination.page >= pagination.totalPages}
                            className="p-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                        >
                            <ChevronRight className="w-3.5 h-3.5 text-gray-600" />
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ReminderHistory;
